import React, { useEffect, useMemo, useState } from "react"

import { format, parseISO } from "date-fns"
import { Plus, Trash2, X } from "lucide-react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  useCreateAvailabilitySlot,
  useDeleteAvailabilitySlot,
  useDepots,
  useUpdateAvailabilitySlot,
  useUpdateLocation,
  useVehicleTypes,
} from "@/hooks/useCollections"
import { formatDayMonthYear } from "@/lib/dateFormats"
import { cn, numberOrNull } from "@/lib/utils"
import {
  AvailabilitySlot,
  Customer,
  DayOfWeek,
  LocationStatus,
  LoadingUnitType,
  Location,
  SlotPnl,
  SlotService,
} from "@/types/collections"
import {
  DAY_ORDER,
  ServiceLine,
  SlotPlan,
  SlotPlanning,
  planningOf,
  reconcileSlots,
  toServiceLines,
} from "@/lib/slotReconcile"

import VehicleTypePicker from "./VehicleTypePicker"

/** A site's agreed collections as one grid: a line per service, a column per day. */

const SERVICE_PRESETS: Array<{ label: string; services: SlotService[]; pnl: SlotPnl[] }> = [
  { label: "ND / 2D", services: ["ND", "2D"], pnl: ["RC1"] },
  { label: "SD", services: ["SD"], pnl: ["RC2"] },
  { label: "ND / 2D (HIVED One)", services: ["ND", "2D"], pnl: ["HIVED One"] },
  { label: "SD (HIVED One)", services: ["SD"], pnl: ["HIVED One"] },
]

const presetLabel = (line: ServiceLine) =>
  SERVICE_PRESETS.find(
    (p) =>
      p.services.slice().sort().join() === line.services.slice().sort().join() &&
      p.pnl.slice().sort().join() === line.pnl.slice().sort().join(),
  )?.label ?? `${line.services.join(" / ") || "No service"} (${line.pnl.join(", ") || "no P&L"})`

const DAY_LABEL: Record<DayOfWeek, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
}

const LOADING_UNIT_TYPES: LoadingUnitType[] = [
  "Pallets (UK)",
  "Pallets (EUR)",
  "Cages",
  "Loose Load",
  "Magnums",
  "Sort Bags",
]

const BLANK_PLANNING: SlotPlanning = planningOf({})

/** Select value standing in for a null depot; a Select item cannot carry an empty value. */
const ANY_SORT_HUB = "any"

const blankLine = (preset: (typeof SERVICE_PRESETS)[number]): ServiceLine => ({
  services: preset.services,
  pnl: preset.pnl,
  isActive: true,
  days: {},
  planning: BLANK_PLANNING,
})

interface SiteSlotsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  location: Location | null
  sites?: Location[]
  onSelectSite?: (location: Location) => void
  customer?: Customer
  slots: AvailabilitySlot[]
  onSaved: () => void
  /** Save seam for tests; unset in the app, where the collections hooks save. */
  persist?: (args: {
    site: Partial<Location>
    plan: SlotPlan
  }) => Promise<void>
}

const SiteSlotsDialog: React.FC<SiteSlotsDialogProps> = ({
  open,
  onOpenChange,
  location,
  sites = [],
  onSelectSite,
  customer,
  slots,
  onSaved,
  persist,
}) => {
  const updateLocation = useUpdateLocation()
  const createSlot = useCreateAvailabilitySlot()
  const updateSlot = useUpdateAvailabilitySlot()
  const deleteSlot = useDeleteAvailabilitySlot()
  const { data: depotsData } = useDepots(open)
  const { data: vehicleTypesData } = useVehicleTypes(open)
  const vehicleTypes = vehicleTypesData?.data ?? []
  const depots = depotsData?.data ?? []

  const [site, setSite] = useState<Partial<Location>>({})
  const [lines, setLines] = useState<ServiceLine[]>([])
  const [saving, setSaving] = useState(false)

  const siteSlots = useMemo(
    () => slots.filter((s) => s.location_id === location?.id),
    [slots, location?.id],
  )

  useEffect(() => {
    if (!open || !location) return
    setSite({
      site_name: location.site_name,
      address: location.address,
      postcode: location.postcode,
      latitude: location.latitude,
      longitude: location.longitude,
      status: location.status,
      loading_unit_type: location.loading_unit_type,
      parcels_per_loading_unit: location.parcels_per_loading_unit,
      allowed_vehicle_types: location.allowed_vehicle_types ?? null,
      max_vehicle_height_m: location.max_vehicle_height_m ?? null,
    })
    setLines(toServiceLines(siteSlots))
  }, [open, location, siteSlots])

  const setCell = (
    lineIndex: number,
    day: DayOfWeek,
    patch: Partial<{ enabled: boolean; time: string; minutes: number }>,
  ) => {
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== lineIndex) return line
        const existing = line.days[day] ?? { enabled: false, time: "", minutes: 15 }
        return { ...line, days: { ...line.days, [day]: { ...existing, ...patch } } }
      }),
    )
  }

  const setPlanning = (lineIndex: number, patch: Partial<SlotPlanning>) => {
    setLines((prev) =>
      prev.map((line, i) =>
        i === lineIndex
          ? { ...line, planning: { ...(line.planning ?? BLANK_PLANNING), ...patch } }
          : line,
      ),
    )
  }

  const fillAcross = (lineIndex: number) => {
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== lineIndex) return line
        const first = DAY_ORDER.map((d) => line.days[d]).find((c) => c?.enabled && c.time)
        if (!first) return line
        const days = { ...line.days }
        for (const day of DAY_ORDER) {
          const cell = days[day]
          if (cell?.enabled) days[day] = { ...cell, time: first.time, minutes: first.minutes }
        }
        return { ...line, days }
      }),
    )
  }

  /** Guards site switches: the grid is local state, so switching would drop edits. */
  const dirty = useMemo(() => {
    if (!location) return false
    const siteChanged = (Object.keys(site) as Array<keyof Location>).some(
      (key) => String(site[key] ?? "") !== String(location[key] ?? ""),
    )
    if (siteChanged) return true
    const plan = reconcileSlots(siteSlots, lines, "")
    return plan.creates.length > 0 || plan.updates.length > 0 || plan.deletes.length > 0
  }, [location, site, siteSlots, lines])

  const incomplete = lines.some((line) =>
    DAY_ORDER.some((d) => {
      const cell = line.days[d]
      return cell?.enabled && !cell.time
    }),
  )

  const handleSave = async () => {
    if (!location) return
    if (incomplete) {
      toast.error("A ticked day needs a time", {
        description: "Every day you collect on needs an agreed arrival, or untick it.",
      })
      return
    }

    setSaving(true)
    try {
      const siteData = {
        ...site,
        latitude: site.latitude != null ? Number(site.latitude) : undefined,
        longitude: site.longitude != null ? Number(site.longitude) : undefined,
        allowed_vehicle_types: site.allowed_vehicle_types?.length
          ? site.allowed_vehicle_types
          : null,
        max_vehicle_height_m: numberOrNull(String(site.max_vehicle_height_m ?? "")),
      }
      const plan = reconcileSlots(siteSlots, lines, new Date().toISOString())

      if (persist) {
        await persist({ site: siteData, plan })
      } else {
        await updateLocation.mutateAsync({ id: location.id, data: siteData })
        for (const data of plan.creates) {
          await createSlot.mutateAsync({ ...data, location_id: location.id } as never)
        }
        for (const { id, data } of plan.updates) {
          await updateSlot.mutateAsync({ id, data: data as never })
        }
        for (const id of plan.deletes) {
          await deleteSlot.mutateAsync(id)
        }
      }

      const changed = plan.creates.length + plan.updates.length + plan.deletes.length
      toast.success("Site saved", {
        description: changed
          ? `${changed} collection ${changed === 1 ? "slot" : "slots"} updated`
          : "No change to the collection days",
      })
      onSaved()
      onOpenChange(false)
    } catch (error) {
      toast.error("Could not save the site", {
        description: error instanceof Error ? error.message : "Unknown error",
      })
    } finally {
      setSaving(false)
    }
  }

  if (!location) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>{location.site_name}</DialogTitle>
          <DialogDescription>
            {customer ? `${customer.name} · ` : ""}
            {location.location_id} — site details and everything we collect from it
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {sites.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-palette-jungleGreen500 mr-1">Site</span>
              {sites.map((option) => {
                const current = option.id === location.id
                return (
                  <Button
                    key={option.id}
                    type="button"
                    size="sm"
                    variant={current ? "default" : "outline"}
                    aria-current={current}
                    disabled={!current && dirty}
                    title={
                      !current && dirty
                        ? "Save or close first — switching would lose the changes on this site"
                        : option.address
                    }
                    className="h-7 text-xs"
                    onClick={() => onSelectSite?.(option)}
                  >
                    {option.site_name}
                  </Button>
                )
              })}
              {dirty && (
                <span className="text-[11px] text-amber-700">
                  Unsaved changes — save or close before switching site
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="site-name">Site name</Label>
              <Input
                id="site-name"
                value={site.site_name ?? ""}
                onChange={(e) => setSite((p) => ({ ...p, site_name: e.target.value }))}
                className="bg-white"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="site-address">Address</Label>
              <Input
                id="site-address"
                value={site.address ?? ""}
                onChange={(e) => setSite((p) => ({ ...p, address: e.target.value }))}
                className="bg-white"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="site-postcode">Postcode</Label>
              <Input
                id="site-postcode"
                value={site.postcode ?? ""}
                onChange={(e) => setSite((p) => ({ ...p, postcode: e.target.value }))}
                className="bg-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="site-lat">Latitude</Label>
              <Input
                id="site-lat"
                value={site.latitude ?? ""}
                onChange={(e) => setSite((p) => ({ ...p, latitude: e.target.value as never }))}
                className="bg-white"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="site-lng">Longitude</Label>
              <Input
                id="site-lng"
                value={site.longitude ?? ""}
                onChange={(e) => setSite((p) => ({ ...p, longitude: e.target.value as never }))}
                className="bg-white"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="site-status">Status</Label>
              <Select
                value={site.status ?? "active"}
                onValueChange={(v) => setSite((p) => ({ ...p, status: v as LocationStatus }))}
              >
                <SelectTrigger id="site-status" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="site-lut">Loading unit type</Label>
              <Select
                value={site.loading_unit_type ?? "Pallets (UK)"}
                onValueChange={(v) =>
                  setSite((p) => ({ ...p, loading_unit_type: v as LoadingUnitType }))
                }
              >
                <SelectTrigger id="site-lut" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {LOADING_UNIT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="site-ppu">Parcels per unit</Label>
              <Input
                id="site-ppu"
                type="number"
                min={0}
                value={site.parcels_per_loading_unit ?? ""}
                onChange={(e) =>
                  setSite((p) => ({ ...p, parcels_per_loading_unit: Number(e.target.value) }))
                }
                className="bg-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="grid gap-2 col-span-2">
              <Label htmlFor="site-vehicle-types">Allowed vehicle types</Label>
              <VehicleTypePicker
                id="site-vehicle-types"
                value={site.allowed_vehicle_types}
                onChange={(v) => setSite((p) => ({ ...p, allowed_vehicle_types: v }))}
                vehicleTypes={vehicleTypes}
                emptyLabel="Any"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="site-max-height">Max vehicle height (m)</Label>
              <Input
                id="site-max-height"
                type="number"
                min={0}
                step={0.01}
                placeholder="No known limit"
                value={site.max_vehicle_height_m ?? ""}
                onChange={(e) =>
                  setSite((p) => ({ ...p, max_vehicle_height_m: e.target.value as never }))
                }
                className="bg-white"
              />
            </div>
          </div>

          <div className="grid gap-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-semibold">Collections</Label>
                <p className="text-xs text-palette-jungleGreen500">
                  Tick the days we collect, and give each one the agreed arrival and how long we
                  are on site. Days that share a time are stored as one slot.
                </p>
              </div>
              <Select
                value=""
                onValueChange={(label) => {
                  const preset = SERVICE_PRESETS.find((p) => p.label === label)
                  if (preset) setLines((prev) => [...prev, blankLine(preset)])
                }}
              >
                <SelectTrigger className="bg-white w-[220px] h-8 text-xs">
                  <span className="flex items-center gap-1.5">
                    <Plus size={12} /> Add a collection
                  </span>
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {SERVICE_PRESETS.map((p) => (
                    <SelectItem key={p.label} value={p.label}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {lines.length === 0 && (
              <div className="text-sm text-palette-jungleGreen500 border rounded-md p-4 text-center">
                Nothing is agreed for this site yet. Add a collection to say when we come.
              </div>
            )}

            {lines.map((line, lineIndex) => {
              const confirmed = siteSlots.filter(
                (s) =>
                  s.confirmed_at &&
                  s.slot_services.slice().sort().join() === line.services.slice().sort().join(),
              ).length
              const planning = line.planning ?? BLANK_PLANNING
              const idPrefix = `line-${lineIndex}`

              return (
                <div key={lineIndex} className="border rounded-md p-3 bg-white grid gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[11px]">
                        {presetLabel(line)}
                      </Badge>
                      <label className="flex items-center gap-1.5 text-[11px] cursor-pointer text-palette-jungleGreen600">
                        <Checkbox
                          checked={line.isActive}
                          onCheckedChange={(checked) =>
                            setLines((prev) =>
                              prev.map((l, i) =>
                                i === lineIndex ? { ...l, isActive: checked === true } : l,
                              ),
                            )
                          }
                          aria-label="This collection is running"
                        />
                        Running
                      </label>
                      {!line.isActive && (
                        <span className="text-[11px] text-amber-700">
                          Paused — the days stay recorded, nothing is checked against them
                        </span>
                      )}
                      {confirmed > 0 && (
                        <span className="text-[11px] text-green-700">
                          {confirmed} confirmed against the shipper agreement
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => fillAcross(lineIndex)}
                      >
                        Same time every day
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                        aria-label="Remove this collection"
                        onClick={() => setLines((prev) => prev.filter((_, i) => i !== lineIndex))}
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                    {DAY_ORDER.map((day) => {
                      const cell = line.days[day]
                      const on = Boolean(cell?.enabled)
                      return (
                        <div
                          key={day}
                          className={cn(
                            "rounded-md border p-2 grid gap-1.5 transition-colors",
                            on
                              ? "border-palette-jungleGreen300 bg-palette-jungleGreen000"
                              : "border-palette-jungleGreen100 bg-slate-50",
                          )}
                        >
                          <label className="flex items-center justify-between text-xs font-medium cursor-pointer">
                            <span>{DAY_LABEL[day]}</span>
                            <Checkbox
                              checked={on}
                              onCheckedChange={(checked) =>
                                setCell(lineIndex, day, { enabled: checked === true })
                              }
                              aria-label={`Collect on ${DAY_LABEL[day]}`}
                            />
                          </label>
                          <Input
                            type="time"
                            aria-label={`${DAY_LABEL[day]} arrival`}
                            value={cell?.time ?? ""}
                            disabled={!on}
                            onChange={(e) => setCell(lineIndex, day, { time: e.target.value })}
                            className={cn(
                              "h-7 text-xs bg-white",
                              on && !cell?.time && "border-red-300",
                            )}
                          />
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min={0}
                              step={5}
                              aria-label={`${DAY_LABEL[day]} minutes on site`}
                              value={cell?.minutes ?? 15}
                              disabled={!on}
                              onChange={(e) =>
                                setCell(lineIndex, day, { minutes: Number(e.target.value) })
                              }
                              className="h-7 text-xs bg-white"
                            />
                            <span className="text-[10px] text-palette-jungleGreen500">min</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="grid gap-1.5">
                      <Label htmlFor={`${idPrefix}-depot`} className="text-xs">
                        Injection depot
                      </Label>
                      <Select
                        value={planning.injection_depot_id ?? ANY_SORT_HUB}
                        onValueChange={(v) =>
                          setPlanning(lineIndex, {
                            injection_depot_id: v === ANY_SORT_HUB ? null : v,
                          })
                        }
                      >
                        <SelectTrigger id={`${idPrefix}-depot`} className="h-8 text-xs bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value={ANY_SORT_HUB}>Any sort hub</SelectItem>
                          {depots
                            .filter((d) => d.is_active || d.id === planning.injection_depot_id)
                            .map((d) => (
                              <SelectItem key={d.id} value={d.id}>
                                {d.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor={`${idPrefix}-vehicle-types`} className="text-xs">
                        Vehicle types
                      </Label>
                      <VehicleTypePicker
                        id={`${idPrefix}-vehicle-types`}
                        value={planning.allowed_vehicle_types}
                        onChange={(v) => setPlanning(lineIndex, { allowed_vehicle_types: v })}
                        vehicleTypes={vehicleTypes}
                        emptyLabel="Any the site allows"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor={`${idPrefix}-units`} className="text-xs">
                        Expected units
                      </Label>
                      <div className="flex items-center gap-1">
                        <Input
                          id={`${idPrefix}-units`}
                          type="number"
                          min={0}
                          placeholder="Unknown"
                          value={planning.expected_units ?? ""}
                          onChange={(e) =>
                            setPlanning(lineIndex, { expected_units: numberOrNull(e.target.value) })
                          }
                          className="h-8 text-xs bg-white"
                        />
                        {site.loading_unit_type && (
                          <span className="whitespace-nowrap text-[10px] text-palette-jungleGreen500">
                            {site.loading_unit_type}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor={`${idPrefix}-priority`} className="text-xs">
                        Priority
                      </Label>
                      <Input
                        id={`${idPrefix}-priority`}
                        type="number"
                        step={1}
                        placeholder="0"
                        value={planning.priority === 0 ? "" : planning.priority}
                        onChange={(e) =>
                          setPlanning(lineIndex, {
                            // Not-null column: blank means 0, ordinary.
                            priority: Math.round(numberOrNull(e.target.value) ?? 0),
                          })
                        }
                        className="h-8 text-xs bg-white"
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor={`${idPrefix}-penalty`} className="text-xs">
                        Miss penalty (£)
                      </Label>
                      <Input
                        id={`${idPrefix}-penalty`}
                        type="number"
                        min={0}
                        step={0.01}
                        placeholder="Default"
                        value={planning.miss_penalty_gbp ?? ""}
                        onChange={(e) =>
                          setPlanning(lineIndex, { miss_penalty_gbp: numberOrNull(e.target.value) })
                        }
                        className="h-8 text-xs bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid gap-1.5">
                    <Label className="text-xs">Excluded dates</Label>
                    <div className="flex flex-wrap items-center gap-2">
                      {(planning.excluded_dates ?? []).map((d) => (
                        <div
                          key={d}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-palette-jungleGreen200 text-xs text-palette-jungleGreen700"
                        >
                          {formatDayMonthYear(parseISO(d))}
                          <button
                            type="button"
                            className="p-0.5 rounded hover:bg-palette-jungleGreen100 text-palette-jungleGreen400 hover:text-palette-jungleGreen600"
                            onClick={() => {
                              const rest = (planning.excluded_dates ?? []).filter((x) => x !== d)
                              setPlanning(lineIndex, { excluded_dates: rest.length ? rest : null })
                            }}
                            aria-label={`Remove ${d}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs bg-white border-palette-jungleGreen200"
                          >
                            Add date
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent variant="plain" align="start">
                          <Calendar
                            mode="single"
                            onSelect={(date) => {
                              if (!date) return
                              const day = format(date, "yyyy-MM-dd")
                              const current = planning.excluded_dates ?? []
                              if (current.includes(day)) return
                              setPlanning(lineIndex, { excluded_dates: [...current, day].sort() })
                            }}
                            className="bg-white"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || incomplete}>
            {saving ? "Saving…" : "Save site"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SiteSlotsDialog
